import type {SiteLite} from '@/multisite/types'

/**
 * Resolve the `brands` array passed to the Jøtul dealer widget.
 *
 * JO2-157: on a brand page (e.g. an ILD landing page) a page-level brand
 * context is supplied as `override`, so the dealer drawer only lists dealers
 * that carry that brand. On a regular page there is no override and we fall
 * back to the site's brand keys (the prior behaviour — unchanged).
 *
 * Returning `undefined` (rather than `[]`) when there is nothing to filter on
 * preserves the previous `site?.brandKeys?.filter(Boolean)` semantics so the
 * widget keeps showing all dealers on non-brand pages.
 */
export const resolveWidgetBrands = (
  override: string[] | null | undefined,
  site: SiteLite | null | undefined,
): string[] | undefined => {
  const fromOverride = (override || []).filter(Boolean)
  if (fromOverride.length > 0) return fromOverride
  return site?.brandKeys?.filter(Boolean)
}

const normalizeWidgetMarket = (market?: string | null): string | undefined => {
  const normalizedMarket = market?.trim().toUpperCase()
  return normalizedMarket && /^[A-Z]{2}$/.test(normalizedMarket) ? normalizedMarket : undefined
}

/**
 * Resolve the ISO 3166-1 alpha-2 markets a Jøtul widget should scope to,
 * from the resolved site. Used by the dealer drawer (plural `markets`) and the
 * warranty form (single `market`, via {@link resolveWidgetMarket}).
 *
 * Drops non-country pseudo-markets, normalizes/validates two-letter codes, and
 * falls back to the locale's region suffix (`sv-SE` → `SE`) only when the site
 * has no explicit market keys. An explicit `GLOBAL`/`INT` key must not become a
 * locale-derived country filter. Returns `undefined` when nothing resolves so
 * the widget keeps its own "no market filter" behaviour rather than receiving
 * `[]`.
 */
export const resolveWidgetMarkets = (site: SiteLite | null | undefined): string[] | undefined => {
  const markets =
    site?.marketKeys?.flatMap((value) => {
      const market = normalizeWidgetMarket(value === 'INT' ? undefined : value)
      return market ? [market] : []
    }) ?? []
  const uniqueMarkets = Array.from(new Set(markets))
  if (uniqueMarkets.length > 0) return uniqueMarkets

  if (site?.marketKeys?.some((market) => Boolean(market?.trim()))) return undefined

  const localeMarket = normalizeWidgetMarket(site?.locale?.split('-')[1])
  if (localeMarket) return [localeMarket]

  return undefined
}

/**
 * The Global website is currently identified by Sanity's isDefaultSite flag.
 * Any site given that flag searches dealers across all markets, regardless of
 * locale or configured market keys; changing the default also changes this scope.
 * Postcodes alone can be ambiguous across countries in this unfiltered search.
 * Other widget types keep the existing site-market resolution.
 */
export const resolveDealerWidgetMarkets = (
  site: SiteLite | null | undefined,
): string[] | undefined => (site?.isDefaultSite ? undefined : resolveWidgetMarkets(site))

/** Product enquiries follow the same default-site scope as the dealer drawer. */
export const resolveProductWidgetMarkets = ({
  market,
  markets,
  locale,
  isDefaultSite,
}: {
  market?: string
  markets?: string[]
  locale?: string
  isDefaultSite?: boolean
}): string[] | undefined => {
  if (isDefaultSite) return undefined

  const normalizedMarkets = Array.from(
    new Set(
      [market, ...(markets ?? [])].flatMap((value) => {
        const normalizedMarket = normalizeWidgetMarket(value)
        return normalizedMarket ? [normalizedMarket] : []
      }),
    ),
  )
  if (normalizedMarkets.length > 0) return normalizedMarkets

  const localeMarket = normalizeWidgetMarket(locale?.split('-')[1])
  if (localeMarket) return [localeMarket]

  return ['NO']
}

/**
 * Single market for widgets that take one ISO code (e.g. the warranty form's
 * `market`, which drives US-specific validation). Returns the first resolved
 * market from {@link resolveWidgetMarkets}, or `undefined`.
 */
export const resolveWidgetMarket = (site: SiteLite | null | undefined): string | undefined =>
  resolveWidgetMarkets(site)?.[0]
